import type { MePermissions, UserRole } from "@/modules/auth/public";

export { hasAnyUserRole, isUserRole } from "@/modules/auth/public";
export type { MePermissions, UserRole } from "@/modules/auth/public";

// Permission 축의 범용 판정이다. Key 를 지정하지 않으면 "게이트 없음"으로 보고 통과시킨다.
// Gnb/PageBase 처럼 도메인을 모르는 셸이 선언적 키로 항목을 거를 때 쓴다.
export function hasMePermission(
  permissions: MePermissions | null,
  key: keyof MePermissions | undefined,
): boolean {
  return key === undefined || permissions?.[key] === true;
}

// Evaluation 화면을 쓸 수 있는지 판정한다. canDeleteShared 와 달리 role 폴백이 없다.
// 어떤 role 도 research 그룹 소속을 대신하지 않는다. Permissions 를 받기 전에는 숨기는 것이
// 안전하다(예: 초기화 중 /v1/me 실패, 크로스탭 세션 동기화). 최종 enforcement 는 서버가 한다.
export function canUseEvaluation(permissions: MePermissions | null): boolean {
  return hasMePermission(permissions, "can_use_evaluation");
}

// 공유 카탈로그(dataset/embodiment/label)를 삭제할 수 있는지 판정한다. Permissions 를 아직
// 받지 못했으면(복원·login 직후 /v1/me 응답 전) admin 여부로 폴백한다. 서버가 최종 권한을 강제한다.
export function canDeleteShared(permissions: MePermissions | null, role: UserRole | null): boolean {
  return permissions ? permissions.can_delete_shared : role === "admin";
}

// 여러 Dataset 을 하나로 통합한다. 원본은 읽기만 하므로 조직 멤버 누구나 통합할 수 있다.
// 통합을 요청한 사람이 결과 Dataset 의 생성자가 된다. 개별 Dataset 의 수정·보관은 행의
// `can_write` 가 정한다.
export function canMergeDatasets(role: UserRole | null): boolean {
  return role === "admin" || role === "member";
}

export function assertCanMergeDatasets(role: UserRole | null): void {
  if (!canMergeDatasets(role)) {
    throw new Error("Dataset 통합은 조직 멤버만 할 수 있습니다.");
  }
}

// 폴더 트리 편집(생성·이름 변경·이동·삭제)과 데이터셋 일괄 폴더 이동을 판정한다. 폴더는
// organization 이 공유하는 분류 구조다. 그래서 데이터셋 생성자와 관계없이 조직 멤버가 정리할 수 있다.
export function canModifyDatasetFolder(role: UserRole | null): boolean {
  return role === "admin" || role === "member";
}

export function assertCanModifyDatasetFolder(role: UserRole | null): void {
  if (!canModifyDatasetFolder(role)) {
    throw new Error("Dataset 폴더 수정은 조직 멤버만 가능합니다.");
  }
}

export function canModifyLabel(role: UserRole | null): boolean {
  return role === "admin";
}

export function assertCanModifyLabel(role: UserRole | null): void {
  if (!canModifyLabel(role)) {
    throw new Error("Label 수정은 admin 권한에서만 가능합니다.");
  }
}

// Embodiment 카탈로그(embodiment·end_effector·tip) 쓰기는 공유 카탈로그 작업이다(admin).
export function canModifyEmbodiment(role: UserRole | null): boolean {
  return role === "admin";
}

export function assertCanModifyEmbodiment(role: UserRole | null): void {
  if (!canModifyEmbodiment(role)) {
    throw new Error("Embodiment 수정은 admin 권한에서만 가능합니다.");
  }
}

// Role 게이트를 **넓히는** permission(OR 축)이다. Key 를 지정하지 않으면 "넓힘 없음"이므로 false 다.
// AND 축의 `hasMePermission` 과 기본값이 반대다. Gnb 항목·PageBase·페이지가 같은 판정을 쓴다.
export function hasOrPermission(
  permissions: MePermissions | null,
  key: keyof MePermissions | undefined,
): boolean {
  return key !== undefined && permissions?.[key] === true;
}

// Operations > Schedule 화면(메뉴·목록·배지)을 여는 research 소속 플래그다. 서버가 계산한다.
// 이 함수는 permission 축만 본다. 등급 축(admin)과의 OR 은 `canViewScheduleOperations` 가 한다.
// Permissions 를 받지 못하면(null) fail closed 로 닫는다.
export function canUseScheduleOperations(permissions: MePermissions | null): boolean {
  return hasMePermission(permissions, "can_use_schedule_operations");
}

// Schedule 열람 게이트의 등급 축이다. Gnb 항목·PageBase `requiredRoles`·페이지 조회 판정이 같은
// 값을 쓴다. 메뉴가 보이는 사람에게 본문이 잠기면 안 된다. 그 반대도 마찬가지다.
export const SCHEDULE_OPERATIONS_ROLES: readonly UserRole[] = ["admin"];

// 등급 OR research 소속으로 판정한다. 두 축은 서로 다른 집단을 허용한다. 그래서 한쪽만으로는
// 모든 대상을 허용할 수 없다.
export function canViewScheduleOperations(
  role: UserRole | null,
  permissions: MePermissions | null,
): boolean {
  return (
    (role !== null && SCHEDULE_OPERATIONS_ROLES.includes(role)) ||
    canUseScheduleOperations(permissions)
  );
}

// 스케줄 쓰기(생성·수정·pause/resume·해제)를 판정한다. BE 가 등급(admin)으로 계산한 플래그를
// 그대로 따른다(`training.schedule_write_forbidden` 미러). 값을 받지 못했거나 구 BE 응답에
// 필드가 없으면 fail closed 로 숨긴다. 열람은 되고 쓰기 버튼만 숨기는 것이 안전하다.
export function canManageSchedules(permissions: MePermissions | null): boolean {
  return hasMePermission(permissions, "can_manage_schedules");
}

// 서버의 admin 쓰기 권한을 요청 전에도 확인한다. 버튼 노출은 can_manage_schedules를 따른다.
export function canWriteSchedule(role: UserRole | null): boolean {
  return role === "admin";
}

export function assertCanWriteSchedule(role: UserRole | null): void {
  if (!canWriteSchedule(role)) {
    throw new Error("Schedule 쓰기는 admin 권한에서만 가능합니다.");
  }
}
